import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { getLocalStorageItem } from '../../../../utils/helper';
import { fetchCompanyScanOverview } from './companyScanApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { SCAN_COLORS, STATUS_RAMP, BREADTH_RAMP, VOLUME_RAMP, scanColor } from './scanTheme';

/**
 * Scans → Overview.
 *
 * Three things carry the page:
 *
 *  1. HOW MANY PEOPLE SCAN AT ALL. Three separate take-up numbers answer three
 *     small questions; the number a company wants is how many of its people
 *     photograph themselves for a health check at all. Counted across the three
 *     collections at once, so somebody who scans their skin in March and their
 *     teeth in April is one person using the feature, not two features.
 *  2. DOES IT WORK. Every one of these scans is an upload handed to an AI
 *     model, and the model can fail, hang, or return a partial result. A
 *     failure rate and a time-to-result are the honest operating picture of
 *     that, and no other section in the panel has anything like it.
 *  3. DO THEY COME BACK. A scan is not a daily habit — nobody photographs
 *     their teeth every morning — so repeat use, not streaks, is what says
 *     whether the feature earned its place.
 *
 * WHAT IS NOT HERE, AND WILL NOT BE: what any scan found. No skin score or
 * risk level, no hygiene score or band, no facial age, no wellbeing reading.
 * Those are health findings about identifiable people, and they were addressed
 * to the person who took the scan.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const ScansOverview = ({ fetcher = fetchCompanyScanOverview, populationLabel = 'users' }) => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const scanBreakdown = stats?.scanBreakdown || [];
  const typesPerUser = stats?.typesPerUser || [];
  const volumeSplit = stats?.volumeSplit || [];
  const statusSplit = stats?.statusSplit || [];
  const reliability = stats?.reliability || {};
  const scansTrend = stats?.scansTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  const trendHasData = scansTrend.some((value) => value > 0);
  const totalScans = stats?.totalScans || 0;

  // Scan mix keeps SCAN_ORDER (the backend's order) rather than ranking, so a
  // scan sits in the same slice position on every page of the section.
  const scanMix = scanBreakdown.map((row) => ({ label: row.label, count: row.scans }));
  const scanMixColors = scanBreakdown.map((row) => scanColor(row.key));

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(scansTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  /**
   * Take-up per scan as one horizontal bar chart. Every bar is a share of the
   * SAME denominator — the whole population — so the three are directly
   * comparable, which a per-scan donut could never be.
   */
  const adoptionOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => scanColor(scanBreakdown[dataPointIndex]?.key)],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '45%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: scanBreakdown.map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` },
      max: 100
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val}% — ${formatBigNumber(scanBreakdown[dataPointIndex]?.users || 0)} people`
      }
    }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Scanning"
          value={stats ? formatBigNumber(stats.activeUsers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Scans Taken"
          value={stats ? formatBigNumber(totalScans) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Scans per Person"
          value={stats ? stats.avgScansPerActiveUser : '-'}
        />
        {/* Breadth: somebody who has tried two of the three is using the
            feature, not testing it. */}
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Used 2+ Scans"
          value={stats ? formatBigNumber(stats.multiScanUsers) : '-'}
        />
        {/* A verdict on the software, and the only verdict on this page. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Analysis Succeeded"
          value={totalScans > 0 ? `${reliability.doneRate ?? 0}%` : '-'}
        />
      </div>

      {/* ---------- Shape of the usage ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Which Scans People Take"
          subtitle="Scans by type"
          data={scanMix}
          colors={scanMixColors}
          unit="scans"
          totalLabel="Scans"
          legendLimit={3}
        />
        <MiniDonutCard
          title="Scan Types per Person"
          subtitle="How many of the three each person has tried"
          data={typesPerUser}
          colors={BREADTH_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={3}
        />
        {/* Not a health verdict — the AI pipeline's own success rate. A scan
            somebody took that never came back is a broken feature, and it is
            invisible if you only count scans. */}
        <MiniDonutCard
          title="Did the Analysis Finish?"
          subtitle="Every scan taken, by outcome"
          data={statusSplit}
          colors={STATUS_RAMP}
          unit="scans"
          totalLabel="Scans"
          legendLimit={3}
        />
      </div>

      {/* ---------- Trend and take-up ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Scanning Over Time" subtitle="Scans and people — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Scans', type: 'column', data: scansTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No scans in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <div className="grid grid-cols-1 gap-6">
          <Panel
            title="Take-up by Scan"
            subtitle={`Share of your ${populationLabel} who have tried each one`}
          >
            {scanBreakdown.length > 0 && stats?.totalUsers > 0 ? (
              <Chart
                type="bar"
                height={180}
                width="100%"
                series={[{ name: 'Take-up', data: scanBreakdown.map((row) => row.adoption) }]}
                options={adoptionOptions}
              />
            ) : (
              <PanelEmpty>Nobody has taken a scan yet.</PanelEmpty>
            )}
          </Panel>

          <MiniDonutCard
            title="Scans per Person"
            subtitle="How often one person comes back"
            data={volumeSplit}
            colors={VOLUME_RAMP}
            unit="people"
            totalLabel="People"
            legendLimit={5}
          />
        </div>
      </div>

      {/* ---------- Scan by scan ---------- */}
      <div className="mt-6">
        <Panel
          title="Scan by Scan"
          subtitle="Reach, volume and whether the analysis finished — never what it found"
        >
          {scanBreakdown.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Scan</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Take-up</th>
                  <th className="py-2 px-4 font-medium text-center">Scans</th>
                  <th className="py-2 px-4 font-medium text-center">Share</th>
                  <th className="py-2 px-4 font-medium text-center">Analysed</th>
                  <th className="py-2 font-medium text-right">Last scan</th>
                </>
              }
            >
              {scanBreakdown.map((row) => (
                <tr
                  key={row.key}
                  className={`${PANEL_ROW_CLASS} cursor-pointer`}
                  onClick={() => navigate(`/${slug}/scans/${row.key}`)}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: SCAN_COLORS[row.key] }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.users)}</td>
                  <td className="py-2 px-4 text-center">{row.adoption}%</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.scans)}</td>
                  <td className="py-2 px-4 text-center">{row.share}%</td>
                  {/* Completion of the ANALYSIS. The failures are named beside
                      it, because a 90% success rate and nine broken scans are
                      the same sentence read two ways. */}
                  <td className="py-2 px-4 text-center">
                    {row.scans > 0 ? (
                      <>
                        {row.completedRate}%
                        {row.failed > 0 ? (
                          <span className="ml-1.5 text-red-500 font-normal">
                            {formatBigNumber(row.failed)} failed
                          </span>
                        ) : null}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 text-right">{row.lastDate || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>Nobody has taken a scan yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

ScansOverview.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default ScansOverview;
